import React, { useState, useEffect } from 'react';
import styles from './Dashboard.module.css';
import { PageBuilder } from './PageBuilder';
import { api, fetchData, sendData, updateData } from '../../../_metronic/helpers/utils/api';

interface TemplatePageEditorProps {
  pageId: string;
}

const TemplatePageEditor: React.FC<TemplatePageEditorProps> = ({ pageId }) => {
  const [pageName, setPageName] = useState('');
  const [slug, setSlug] = useState('');
  const [metadataId, setMetadataId] = useState<number | null>(null);

  const [seoTitle, setSeoTitle] = useState('');
  const [seoTitleId, setSeoTitleId] = useState<number | null>(null);
  const [seoDescription, setSeoDescription] = useState('');
  const [seoDescriptionId, setSeoDescriptionId] = useState<number | null>(null);

  const getFrontendUrl = (path: string) => {
    const hostname = window.location.hostname;
    let frontendHost = hostname;
    
    if (hostname.startsWith('admin.')) {
      frontendHost = hostname.replace('admin.', '');
    } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `http://${hostname}:3000${path}`;
    }
    
    const protocol = window.location.protocol;
    return `${protocol}//${frontendHost}${path}`;
  };

  useEffect(() => {
    const loadPageData = async () => {
      try {
        const pagesRes = await api.get('page');
        const pages = pagesRes.data?.allPages || [];
        const page = pages.find((p: any) => String(p.id) === pageId);
        
        if (page) {
          setSlug(`/${page.name}`);
          setPageName(page.name);
          
          const metadata = await fetchData({ pageId: Number(pageId), sectionName: 'PageMetadata', key: 'title' })();
          if (metadata && metadata.valueEnglish) {
            setPageName(metadata.valueEnglish as string);
            setMetadataId(metadata.id);
          } else {
            setMetadataId(null);
          }

          const seoTitleData = await fetchData({ pageId: Number(pageId), sectionName: 'PageMetadata', key: 'seoTitle' })();
          if (seoTitleData && seoTitleData.valueEnglish) {
            setSeoTitle(seoTitleData.valueEnglish as string);
            setSeoTitleId(seoTitleData.id);
          } else {
            setSeoTitle('');
            setSeoTitleId(null);
          }

          const seoDescData = await fetchData({ pageId: Number(pageId), sectionName: 'PageMetadata', key: 'seoDescription' })();
          if (seoDescData && seoDescData.valueEnglish) {
            setSeoDescription(seoDescData.valueEnglish as string);
            setSeoDescriptionId(seoDescData.id);
          } else {
            setSeoDescription('');
            setSeoDescriptionId(null);
          }
        } else {
          // If the page doesn't exist, clear all fields
          setSlug('');
          setPageName('');
          setSeoTitle('');
          setSeoDescription('');
        }
      } catch (err) {
        console.error('Failed to load page data', err);
      }
    };
    loadPageData();
  }, [pageId]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageName(e.target.value);
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlug(e.target.value);
  };

  const saveMeta = async (key: string, value: string, currentId: number | null, setId: (id: number) => void) => {
    try {
      const body = {
        pageId: Number(pageId),
        sectionName: 'PageMetadata',
        key,
        valueEnglish: value
      };
      if (currentId) {
        await updateData({ id: currentId, body });
      } else {
        const res = await sendData(body);
        if (res && res.id) setId(res.id);
      }
    } catch (e) {
      console.error(`Failed to save ${key}`, e);
    }
  };

  const handleSaveName = async () => {
    await saveMeta('title', pageName, metadataId, setMetadataId);
    window.dispatchEvent(new Event('template_pages_updated'));
  };
  
  const handleSaveSeoTitle = () => saveMeta('seoTitle', seoTitle, seoTitleId, setSeoTitleId);
  const handleSaveSeoDesc = () => saveMeta('seoDescription', seoDescription, seoDescriptionId, setSeoDescriptionId);

  return (
    <>
      {/* Page Configuration Box */}
      <div className="card shadow-sm mb-8 w-100 border border-light">
        <div className="card-body p-6 d-flex flex-column gap-6">
          <div className="d-flex align-items-center gap-3 border-bottom pb-4">
            <div className="d-flex justify-content-center align-items-center rounded bg-light-primary w-40px h-40px">
              <i className="bi bi-gear fs-3 text-primary"></i>
            </div>
            <div className="d-flex flex-column">
              <h3 className="fw-bolder m-0 text-dark fs-5">Page Settings & SEO</h3>
              <span className="text-muted fs-8">Configure your custom page details and meta tags</span>
            </div>
          </div>
          
          <div className="row g-6">
            <div className="col-12 col-md-6">
              <label className="fs-7 fw-bold text-gray-700 mb-2 required">Page Name</label>
              <input
                type="text"
                className="form-control form-control-solid"
                placeholder="Page Name"
                value={pageName}
                onChange={handleNameChange}
                onBlur={handleSaveName}
              />
            </div>
            
            <div className="col-12 col-md-6">
              <label className="fs-7 fw-bold text-gray-700 mb-2">URL Slug</label>
              <div className="input-group input-group-solid">
                <span className="input-group-text border-0 text-muted pe-1">/</span>
                <input
                  type="text"
                  className="form-control form-control-solid ps-1 border-0"
                  placeholder="slug"
                  value={slug.replace(/^\//, '')}
                  readOnly
                  title="Slug editing is disabled to prevent broken links. Create a new page instead."
                />
                {slug && (
                  <a 
                    href={getFrontendUrl(slug)} 
                    target="_blank" 
                    rel="noreferrer"
                    className="btn btn-light-dark"
                    title="View Live Page"
                  >
                    <i className="bi bi-box-arrow-up-right text-gray-800 p-0"></i>
                  </a>
                )}
              </div>
            </div>
            
            <div className="col-12 col-md-6">
              <label className="fs-7 fw-bold text-gray-700 mb-2">SEO Meta Title</label>
              <input
                type="text"
                className="form-control form-control-solid"
                placeholder="Optimal length: 50-60 characters"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                onBlur={handleSaveSeoTitle}
              />
              <div className="text-muted fs-8 mt-2">This is the title that will appear on search engines like Google.</div>
            </div>
            
            <div className="col-12 col-md-6">
              <label className="fs-7 fw-bold text-gray-700 mb-2">SEO Meta Description</label>
              <input
                type="text"
                className="form-control form-control-solid"
                placeholder="Optimal length: 150-160 characters"
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value)}
                onBlur={handleSaveSeoDesc}
              />
              <div className="text-muted fs-8 mt-2">A brief description of the page content for search engine results.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Page Builder Area */}
      <PageBuilder pageId={pageId} slug={slug} />
    </>
  );
};

export default TemplatePageEditor;
